import { eventQueries } from "@/components/calendars/api.ts";
import { SectionIcon } from "@/components/calendars/eventPanelFields.tsx";
import { Sparkle } from "@/components/icons/customIcons.tsx";
import { Button } from "@/components/ui/button.tsx";
import { GradientDefinition } from "@/components/ui/gradient-def.tsx";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Link, useLocation } from "wouter";

/**
 * The brief Okthx wrote for this meeting, as the event panel's own row.
 *
 * The row sits directly above the meeting link, so prep reads before the call
 * it prepares for. It owns the whole row — leading icon included — rather than
 * being dropped into one the panel builds, because it renders nothing for the
 * meetings nobody briefed, which is most of them. A parent cannot see that the
 * component returned null, so a parent-owned wrapper would leave a bare icon
 * and a section gap on every unbriefed event.
 *
 * Two icons, saying two different things. The sparkle leads the row in the slot
 * `CalendarDays`, `Repeat` and `Video` fill on the rows around it, where it
 * names what the whole section is: something Okthx made. The document sits on
 * the button, because the button opens a document — and because the calendar
 * chip marks a briefed meeting with the same glyph, so one shape means "the
 * brief" wherever it appears.
 *
 * The button's icon is stroked with a gradient rather than `currentColor`.
 * `gradientOutline` paints its label by making the text transparent and
 * clipping a gradient to it, and that reaches text alone — an icon left on
 * `currentColor` inherits the transparency and vanishes. `group-hover` hands it
 * to white at the same moment the button fills. The row's leading icon is
 * outside the button and needs none of this; it takes the muted grey every
 * other section icon uses.
 *
 * `size="xs"` is the size the Join button below it uses, so the two rows agree
 * on height without a bespoke class.
 *
 * `dismissPanel` settles the surface the panel is on — unsaved-changes prompt
 * included — and calls back once the user has committed to leaving, at which
 * point the navigation runs. An overlay surface closes itself there rather than
 * staying in front of the brief it just opened.
 *
 * It stays an anchor so the destination survives as a real link. Only the plain
 * left click is intercepted; a cmd-click still opens a new tab, where
 * dismissing the panel the user is staying on would be wrong.
 *
 * Every class sits on `Button` rather than the `Link` it wraps: `asChild`
 * concatenates the two classNames instead of merging them, so a layout class
 * put on the child would race the base button styles it means to override.

 */
export const EventBriefSection = ({
  eventId,
  dismissPanel,
}: {
  eventId: string | undefined;
  dismissPanel: (proceed: () => void) => void;
}) => {
  const { data: briefs } = useQuery(eventQueries.briefs(eventId));
  const [, setLocation] = useLocation();

  if (!briefs?.length) return null;

  return (
    <div className="flex items-center gap-3">
      <SectionIcon icon={Sparkle} label="Meeting brief" />
      <GradientDefinition />
      {/* Wraps rather than overflowing. Only one brief is ever pinned to a
          meeting today — re-briefing replaces the pin — but the response is a
          list, and a second one should push a line down rather than off. */}
      <div className="flex flex-wrap items-center gap-2">
        {briefs.map((brief) => {
          const href = `/artifacts/${brief.artifactType}/${brief.artifactId}`;
          return (
            // The white behind the button, which the button cannot paint for
            // itself: `gradientOutline` shows its label by clipping a gradient
            // to the text, and `background-clip: text` clips every background
            // layer — a `bg-card` on the button would be clipped to the glyphs
            // and paint nothing. So the surface sits behind it, matching the
            // button's own pill radius. Without it the panel's beige reads
            // straight through the button, which is most obvious on mobile
            // where the panel fills the screen.
            <span key={brief.artifactId} className="inline-flex rounded-full bg-card">
              <Button
                asChild
                variant="gradientOutline"
                size="xs"
                data-statsig-name="event-open-brief"
                className="group gap-1.5"
              >
                <Link
                  href={href}
                  // Leads with the visible label: WCAG 2.5.3 wants the accessible
                  // name to contain it, so voice control can act on what it reads.
                  aria-label={`Meeting brief Okthx wrote for ${brief.title}`}
                  onClick={(e) => {
                    // A new-tab intent leaves the user on the panel, so there is
                    // nothing to dismiss — let the browser have it.
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    dismissPanel(() => setLocation(href));
                  }}
                >
                  <FileText className="size-3.5 shrink-0 gradient-brand-purple-stroke group-hover:stroke-white" />
                  <span className="text-sm">Meeting brief</span>
                </Link>
              </Button>
            </span>
          );
        })}
      </div>
    </div>
  );
};
